import * as Sentry from "@sentry/nextjs";
import type { GetStepTools } from "inngest";
import type { ZodSchema, z } from "zod";
import type { ThrottleStrategy } from "@/lib/medplum/retry";
import { createLogger } from "../logger";
import { inngest } from "./client";

type InngestDebounceConfig = Parameters<typeof inngest.createFunction>[0] extends {
  debounce?: infer D;
}
  ? D
  : never;

type InngestCancelOnConfig = Parameters<typeof inngest.createFunction>[0] extends {
  cancelOn?: infer C;
}
  ? C
  : never;

type InngestConcurrencyConfig = Parameters<typeof inngest.createFunction>[0] extends {
  concurrency?: infer C;
}
  ? C
  : never;

type InngestOnFailureHandler = Parameters<typeof inngest.createFunction>[0] extends {
  onFailure?: infer F;
}
  ? F
  : never;

type InngestPriorityConfig = Parameters<typeof inngest.createFunction>[0] extends {
  priority?: infer P;
}
  ? P
  : never;

type InngestRetriesConfig = Parameters<typeof inngest.createFunction>[0] extends {
  retries?: infer R;
}
  ? R
  : never;

type InngestRateLimitConfig = Parameters<typeof inngest.createFunction>[0] extends {
  rateLimit?: infer R;
}
  ? R
  : never;

type InngestThrottleConfig = Parameters<typeof inngest.createFunction>[0] extends {
  throttle?: infer T;
}
  ? T
  : never;

type InngestSingletonConfig = Parameters<typeof inngest.createFunction>[0] extends {
  singleton?: infer S;
}
  ? S
  : never;

type InngestTimeoutsConfig = Parameters<typeof inngest.createFunction>[0] extends {
  timeouts?: infer T;
}
  ? T
  : never;

export interface BackgroundJobConfig<T extends ZodSchema> {
  event?: string;
  /** CEL condition on the event trigger. Only matching events start a run. */
  eventIf?: string;
  id: string;
  schema?: T;
  idempotency?: string;
  cron?: string;
  debounce?: InngestDebounceConfig;
  cancelOn?: InngestCancelOnConfig;
  concurrency?: InngestConcurrencyConfig;
  onFailure?: InngestOnFailureHandler;
  /** Inngest priority config. Use InngestPriority constants from client.ts. */
  priority?: InngestPriorityConfig;
  /** Inngest retry count. Defaults to Inngest's built-in default (currently 3). */
  retries?: InngestRetriesConfig;
  /** Inngest rate limit config: at-most-one run per key per window. */
  rateLimit?: InngestRateLimitConfig;
  /** Inngest singleton config: exclusive execution for the full function run. */
  singleton?: InngestSingletonConfig;
  /** Inngest run caps. `finish` is wall clock after the run starts. */
  timeouts?: InngestTimeoutsConfig;
  /** Inngest throttle config: queues runs past `limit` per `period` instead of dropping them. */
  throttle?: InngestThrottleConfig;
  /**
   * Where Medplum rate-limit waits should happen. Defaults to `"durable"` so
   * throttle delays are scheduled by Inngest instead of burning invocation time.
   */
  throttling?: ThrottleStrategy;
  handler: (props: {
    payload: z.infer<T>;
    step: GetStepTools<typeof inngest>;
    logger: ReturnType<typeof createLogger>;
    runId?: string;
    /** Inngest attempt number for this run (0-based). */
    attempt?: number;
    /** The Inngest event ID that triggered this job, for telemetry correlation. */
    inngestEventId?: string;
    /** Where Medplum rate-limit waits happen for this job. */
    throttling: ThrottleStrategy;
  }) => Promise<unknown>;
}

interface BackgroundJob<T extends ZodSchema> {
  event: string;
  job: ReturnType<typeof inngest.createFunction>;
  trigger: (payload: z.input<T>, id?: string) => ReturnType<typeof inngest.send>;
}

/**
 * Parses and validates background job payloads with optional Zod schemas.
 */
function parseJobPayload<T extends ZodSchema>({
  jobId,
  logger,
  payload,
  schema,
  hasEvent,
  runId,
  attempt,
}: {
  jobId: string;
  logger: ReturnType<typeof createLogger>;
  payload: unknown;
  schema: T | undefined;
  hasEvent: boolean;
  runId?: string;
  attempt?: number;
}): z.infer<T> {
  if (!hasEvent || !schema) {
    return payload as z.infer<T>;
  }

  const parseResult = schema.safeParse(payload);
  if (!parseResult.success) {
    // Shape only, never values: job payloads carry PHI (transcripts, names) and this line
    // reaches BetterStack and Sentry. The zod error and its messages can echo the input.
    logger.error("Invalid background job payload", {
      jobId,
      runId,
      attempt,
      issues: describeIssues(parseResult.error.issues),
      payloadKeys: topLevelKeys(payload),
      payloadBytes: serializedLength(payload),
    });
    throw new Error(`Invalid payload for ${jobId}`);
  }

  return parseResult.data;
}

/** Zod issues without messages or values: path, code, and the expected type only. */
function describeIssues(issues: z.core.$ZodIssue[]) {
  return issues.map((issue) => ({
    path: issue.path.map(String).join("."),
    code: issue.code,
    expected: "expected" in issue ? String(issue.expected) : undefined,
  }));
}

function topLevelKeys(payload: unknown): string[] | null {
  if (payload === null || typeof payload !== "object" || Array.isArray(payload)) return null;
  return Object.keys(payload);
}

function serializedLength(payload: unknown): number | null {
  try {
    return JSON.stringify(payload)?.length ?? null;
  } catch {
    return null;
  }
}

/**
 * Validates trigger payloads against an optional Zod schema.
 */
function validateTriggerPayload<T extends ZodSchema>({
  payload,
  schema,
}: {
  payload: z.input<T>;
  schema: T | undefined;
}): void {
  if (!schema) {
    return;
  }

  const parseResult = schema.safeParse(payload);
  if (parseResult.success) {
    return;
  }

  throw parseResult.error;
}

/**
 * Wraps an Inngest step tools object so every `step.run` call emits a debug
 * log with its duration. This makes per-step latency observable without
 * touching individual jobs — most job time lives inside steps, so a single
 * "completed in N ms" total isn't enough to spot regressions.
 *
 * Other step methods (`sleep`, `waitForEvent`, etc.) pass through unchanged.
 */
function wrapStepWithTiming<S extends GetStepTools<typeof inngest>>(
  step: S,
  logger: ReturnType<typeof createLogger>,
): S {
  // Skip wrapping when step.run is missing, when logger.debug is unavailable,
  // or when step.run is a test spy (has `.mock`). Timing is purely observational
  // and must never interfere with handler tests that assert on step.run spies.
  if (typeof step?.run !== "function" || typeof logger.debug !== "function" || "mock" in step.run) {
    return step;
  }

  const originalRun = step.run.bind(step) as S["run"];
  const timedRun: S["run"] = (async (stepId: Parameters<S["run"]>[0], ...rest: unknown[]) => {
    const stepStartMs = Date.now();
    const [fn, ...input] = rest;
    // Logged when the step's code actually runs, not when a replay returns its memoized result.
    // A start with no "Step completed" names the step Vercel's 800s limit cut off.
    const loggedFn =
      typeof fn === "function"
        ? (...args: unknown[]) => {
            logger.debug("Step started", { stepId });
            return fn(...args);
          }
        : fn;
    try {
      const result = await (originalRun as (...args: unknown[]) => Promise<unknown>)(
        stepId,
        loggedFn,
        ...input,
      );
      logger.debug("Step completed", {
        stepId,
        durationMs: Date.now() - stepStartMs,
      });
      return result;
    } catch (error) {
      logger.debug("Step failed", {
        stepId,
        durationMs: Date.now() - stepStartMs,
      });
      throw error;
    }
  }) as S["run"];

  return new Proxy(step, {
    get(target, prop, receiver) {
      if (prop === "run") return timedRun;
      return Reflect.get(target, prop, receiver);
    },
  });
}

/**
 * Builds an `onFailure` handler that always runs after Inngest exhausts all
 * retries. Emits a distinct error log and Sentry tag so alerting can fire on
 * "actually broken" instead of every transient retry, then chains to any
 * caller-provided `onFailure` to preserve domain-specific recovery logic.
 */
function createOnFailureHandler({
  jobId,
  logger,
  userOnFailure,
}: {
  jobId: string;
  logger: ReturnType<typeof createLogger>;
  userOnFailure: InngestOnFailureHandler | undefined;
}): InngestOnFailureHandler {
  return (async (args: Parameters<NonNullable<InngestOnFailureHandler>>[0]) => {
    const { event, error } = args;
    const failedEvent = (event?.data as { event?: { id?: string } } | undefined)?.event;
    const inngestEventId = failedEvent?.id;

    Sentry.getCurrentScope().setTags({
      "inngest.retries_exhausted": true,
      "inngest.job_id": jobId,
    });

    logger.error("Background job retries exhausted", {
      jobId,
      inngestEventId,
      error,
    });

    try {
      if (userOnFailure) {
        await (userOnFailure as (a: typeof args) => Promise<unknown> | unknown)(args);
      }
    } finally {
      // Failure callbacks are short-lived requests; flush before Vercel freezes the process.
      // Tolerate test loggers that don't implement `.flush()` — same fallback as `.with()` below.
      // A throw here would replace the domain failure, losing the very signal we're flushing for.
      if (typeof logger.flush === "function") {
        await logger.flush();
      }
    }
  }) as InngestOnFailureHandler;
}

/**
 * Creates a background job with type-safe event handling
 *
 * @param config.event - The event name.
 * @param config.id - The job ID.
 * @param config.schema - The schema for the event.
 * @param config.idempotency - Optional idempotency configuration (CEL expression).
 *   Ignored when `config.cron` is set — see the note at the `createFunction` call.
 * @param config.debounce - Optional debounce configuration to delay execution until events stop arriving.
 * @param config.cancelOn - Optional cancel configuration to automatically cancel the function when specified events are received.
 * @param config.singleton - Optional exclusive-execution configuration for full function runs.
 * @param config.handler - The handler function for the job.
 * @returns Object with job definition and trigger function to send events
 */
export function createBackgroundJob<T extends ZodSchema>(
  config: BackgroundJobConfig<T>,
): BackgroundJob<T> {
  if (!config.event && !config.cron) {
    throw new Error("Either event or cron must be provided");
  }

  const logger = createLogger(config.id);

  return {
    /**
     * The event name that triggers this job (empty string for cron-only jobs).
     */
    event: config.event ?? "",

    /**
     * Job definition.
     */
    job: inngest.createFunction(
      {
        id: config.id,
        // Inngest compiles `idempotency` into a 24h rate limit keyed on the CEL
        // expression. Cron-generated events don't expose `event.id` to that
        // expression, so every fire hashes to the same bucket and only the first
        // one each day is admitted — a `*/2` cron runs once a day, not 720x.
        // Function-level config can't distinguish triggers, so a job with a cron
        // trigger gets no idempotency at all, including on its event trigger.
        ...(config.idempotency && !config.cron && { idempotency: config.idempotency }),
        ...(config.priority && { priority: config.priority }),
        ...(config.retries != null && { retries: config.retries }),
        ...(config.rateLimit && { rateLimit: config.rateLimit }),
        ...(config.debounce && { debounce: config.debounce }),
        ...(config.cancelOn && { cancelOn: config.cancelOn }),
        ...(config.concurrency && { concurrency: config.concurrency }),
        ...(config.singleton && { singleton: config.singleton }),
        ...(config.timeouts && { timeouts: config.timeouts }),
        ...(config.throttle && { throttle: config.throttle }),
        onFailure: createOnFailureHandler({
          jobId: config.id,
          logger,
          userOnFailure: config.onFailure,
        }),
      },
      [
        config.event
          ? { event: config.event, ...(config.eventIf && { if: config.eventIf }) }
          : undefined,
        config.cron ? { cron: config.cron } : undefined,
      ].filter((trigger) => trigger !== undefined),
      async ({ event, step, runId, attempt }) => {
        const inngestEventId = event?.id;
        // Tolerate test loggers that don't implement `.with()` — fall back to the
        // base logger so legacy mocks keep working without per-test boilerplate.
        const runLogger =
          typeof logger.with === "function"
            ? logger.with({ runId, attempt, inngestEventId })
            : logger;

        runLogger.info("Running background job");

        const hasEvent = event !== undefined;
        const payload = parseJobPayload({
          jobId: config.id,
          logger: runLogger,
          payload: event?.data,
          schema: config.schema,
          hasEvent,
          runId,
          attempt,
        });

        const throttling: ThrottleStrategy = config.throttling ?? "durable";
        const startMs = Date.now();
        const timedStep = wrapStepWithTiming(step, runLogger);
        try {
          const result = await config.handler({
            payload,
            step: timedStep,
            logger: runLogger,
            runId,
            attempt,
            inngestEventId,
            throttling,
          });
          runLogger.info("Background job completed", {
            durationMs: Date.now() - startMs,
          });
          return result;
        } catch (error) {
          runLogger.error("Background job failed", {
            durationMs: Date.now() - startMs,
            error,
          });
          throw error;
        }
      },
    ),

    /**
     * Triggers the background function.
     *
     * @param payload T - the payload for the job call.
     */
    trigger(payload: z.input<T>, id?: string) {
      try {
        if (!config.event) throw new Error(`${config.id} does not has a trigger event`);
        validateTriggerPayload({ payload, schema: config.schema });
        return inngest.send({ name: config.event, data: payload, ...(id && { id }) });
      } catch (error) {
        logger.error("Failed to trigger background job", { error });
        throw new Error(`Failed to trigger ${config.id}`);
      }
    },
  };
}
